import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthProvider";
import { LanguageProvider } from "./context/LanguageProvider";
import { ThemeProvider } from "./context/ThemeProvider";
import { appConfig } from "./apiConfig";
import ScrollToTop from "./ScrollToTop";

// Layouts
import DashboardLayout from "./layout/DashboardLayout";
import DefaultLayout from "./layout/DefaultLayout";
import AuthLayout from "./layout/AuthLayout";
import WebLayout from "./layout/WebLayout";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";

//Default Pages
import LinksPage from "./websites/nextlead/links/page";

// Dashboard Pages
import Dashboard from "./pages/dashboard/Dashboard";
import Orders from "./pages/orders/orders";
import OrdersUpdate from "./pages/orders/ordersUpdate";
import Products from "./pages/products/products";
import ProductDetail from "./pages/products/productDetail";
import ProductCreate from "./pages/products/productCreate";
import ProductUpdate from "./pages/products/productUpdate";
import Varianrs from "./pages/variants/variants";
import VarianrsCreate from "./pages/variants/variantsCreate";
import VarianrsUpdate from "./pages/variants/variantsUpdate";
import Categories from "./pages/categories/categories";
import CategoriesCreate from "./pages/categories/categoriesCreate";
import CategoriesUpdate from "./pages/categories/categoriesUpdate";
import Cities from "./pages/cities/cities";
import Configs from "./pages/configs/configs";
import Scan_Retour from "./pages/scan/scan_retour";
import Scan_Ramassage from "./pages/scan/scan_ramassage";
import Admins from "./pages/admins/admins";
import AdminsUpdate from "./pages/admins/adminUpdate";
import Saller from "./pages/sallers/sallers";
import SallerUpdate from "./pages/sallers/sallersUpdate";
import Confirmators from "./pages/confirmators/confirmators";
import ConfirmatorsUpdate from "./pages/confirmators/confirmatorsUpdate";
import Fournisseurs from "./pages/fournisseurs/fournisseurs";
import FournisseursCreate from "./pages/fournisseurs/fournisseursCreate";
import FournisseursUpdate from "./pages/fournisseurs/fournisseursUpdate";
import Livreurs from "./pages/livreurs/livreurs";
import LivreursUpdate from "./pages/livreurs/livreursUpdate";
import Roles from "./pages/roles/roles";
import RolesCreate from "./pages/roles/rolesCreate";
import RolesUpdate from "./pages/roles/rolesUpdate";
import BlackList from "./pages/blacklist/blackList";
import Payments from "./pages/payments/payments";
import PaymentsSallers from "./pages/payments/sallers/page";
import PaymentsConfirmators from "./pages/payments/confirmators/page";
import PaymentsLivreurs from "./pages/payments/livreurs/page";
import PaymentsSystem from "./pages/payments/system/page";
import IntegrationDelivery from "./pages/integration/delivery/page";
import IntegrationApps from "./pages/integration/apps/page";
import ApiDocs from "./pages/integration/api/page";
import Reclamations from "./pages/reclamations/page";
import ActivityLogs from "./pages/activity-logs/page";
import Annonces from "./pages/annonces/page";
import AnnoncesCreate from "./pages/annonces/create/page";
import AnnoncesUpdate from "./pages/annonces/update/page";
import Cart from "./pages/cart/page";
import Profile from "./pages/profile/page";
import UserCreate from "./pages/users/create/page";

const Home = lazy(() => import(`./websites/${appConfig.slug}/page`));
const About = lazy(() => import(`./websites/${appConfig.slug}/about/page`));
const Contact = lazy(() => import(`./websites/${appConfig.slug}/contact/page`));
const Policy = lazy(() => import(`./websites/${appConfig.slug}/policy/page`));



const AppRoutes = () => {
  const { token } = useAuth();

  return (
    <Suspense fallback={<div className="h-screen"></div>}>
      <Routes>
        
        <Route element={<WebLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<Contact />} />
          <Route path="policy" element={<Policy />} />
        </Route>

        <Route element={<AuthLayout />}>
          <Route path="login" element={ <LoginPage /> }
          />
          <Route path="register" element={ <RegisterPage /> }
          />
          <Route path="forgot-password" element={ <ForgotPasswordPage /> } />
        </Route>

        <Route element={<DefaultLayout />}>
          <Route path="/links-social-media" element={<LinksPage />} />
        </Route>

        <Route element={token ? <DashboardLayout /> : <Navigate to="/login" replace />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="orders/:status" element={<Orders />} />
          <Route path="orders/update/:id" element={<OrdersUpdate />} />
          <Route path="products" element={<Products />} />
          <Route path="products/:id" element={<ProductDetail />} />
          <Route path="products/create" element={<ProductCreate />} />
          <Route path="products/update/:id" element={<ProductUpdate />} />
          <Route path="variants" element={<Varianrs />} />
          <Route path="variants/create" element={<VarianrsCreate />} />
          <Route path="variants/update/:id" element={<VarianrsUpdate />} />
          <Route path="categories" element={<Categories />} />
          <Route path="categories/create" element={<CategoriesCreate />} />
          <Route path="categories/update/:id" element={<CategoriesUpdate />} />
          <Route path="cities" element={<Cities />} />
          <Route path="configs" element={<Configs />} />
          <Route path="scan-retour" element={<Scan_Retour />} />
          <Route path="scan-ramassage" element={<Scan_Ramassage />} />
          <Route path="admins" element={<Admins />} />
          <Route path="admins/:id" element={<AdminsUpdate />} />
          <Route path="sallers" element={<Saller />} />
          <Route path="sallers/:id" element={<SallerUpdate />} />
          <Route path="confirmators" element={<Confirmators />} />
          <Route path="confirmators/:id" element={<ConfirmatorsUpdate />} />
          <Route path="fournisseurs" element={<Fournisseurs />} />
          <Route path="fournisseurs/create" element={<FournisseursCreate />} />
          <Route path="fournisseurs/update/:id" element={<FournisseursUpdate />} />
          <Route path="livreurs" element={<Livreurs />} />
          <Route path="livreurs/update/:id" element={<LivreursUpdate />} />
          <Route path="roles" element={<Roles />} />
          <Route path="roles/create" element={<RolesCreate />} />
          <Route path="roles/update/:id" element={<RolesUpdate />} />
          <Route path="black-list" element={<BlackList />} />
          <Route path="payments" element={<Payments />} />
          <Route path="payments/sallers" element={<PaymentsSallers />} />
          <Route path="payments/confirmators" element={<PaymentsConfirmators />} />
          <Route path="payments/livreurs" element={<PaymentsLivreurs />} />
          <Route path="payments/system" element={<PaymentsSystem />} />
          <Route path="integration/delivery" element={<IntegrationDelivery />} />
          <Route path="integration/applications" element={<IntegrationApps />} />
          <Route path="integration/apis" element={<ApiDocs />} />
          <Route path="reclamations" element={<Reclamations />} />
          <Route path="activity-logs" element={<ActivityLogs />} />
          <Route path="annonces" element={<Annonces />} />
          <Route path="annonces/create" element={<AnnoncesCreate />} />
          <Route path="annonces/update/:id" element={<AnnoncesUpdate />} />
          <Route path="cart" element={<Cart />} />
          <Route path="profile" element={<Profile />} />
          <Route path="users/create" element={<UserCreate />} />
        </Route>

        <Route path="*" element={<Navigate to={token ? "/dashboard" : "/"} replace />} />
        
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
          <ThemeProvider>
            <ScrollToTop />
            <AppRoutes />
          </ThemeProvider>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;



// import { useState, lazy, Suspense } from "react";
// import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// import { AuthProvider, useAuth } from "./context/AuthProvider";
// import { LanguageProvider } from "./context/LanguageProvider";
// import { appConfig } from "./apiConfig";
// import ScrollToTop from "./ScrollToTop";

// // Layouts
// import DashboardLayout from "./layout/DashboardLayout";
// import AuthLayout from "./layout/AuthLayout";
// import WebLayout from "./layout/WebLayout";

// // Auth Pages
// import LoginPage from "./pages/auth/LoginPage";
// import RegisterPage from "./pages/auth/RegisterPage";

// // Dashboard Pages
// import Dashboard from "./pages/dashboard/Dashboard";
// import Orders from "./pages/orders/orders";
// import OrdersUpdate from "./pages/orders/ordersUpdate";
// import Products from "./pages/products/products";
// import ProductDetail from "./pages/products/productDetail";
// import ProductCreate from "./pages/products/productCreate";
// import ProductUpdate from "./pages/products/productUpdate";
// import Varianrs from "./pages/variants/variants";
// import VarianrsCreate from "./pages/variants/variantsCreate";
// import VarianrsUpdate from "./pages/variants/variantsUpdate";
// import Categories from "./pages/categories/categories";
// import CategoriesCreate from "./pages/categories/categoriesCreate";
// import CategoriesUpdate from "./pages/categories/categoriesUpdate";
// import Cities from "./pages/cities/cities";
// import Configs from "./pages/configs/configs";
// import Scan from "./pages/scan/scan";
// import Admins from "./pages/admins/admins";
// import AdminsUpdate from "./pages/admins/adminUpdate";
// import Saller from "./pages/sallers/sallers";
// import SallerUpdate from "./pages/sallers/sallersUpdate";
// import Confirmators from "./pages/confirmators/confirmators";
// import ConfirmatorsUpdate from "./pages/confirmators/confirmatorsUpdate";
// import Fournisseurs from "./pages/fournisseurs/fournisseurs";
// import FournisseursCreate from "./pages/fournisseurs/fournisseursCreate";
// import FournisseursUpdate from "./pages/fournisseurs/fournisseursUpdate";
// import Livreurs from "./pages/livreurs/livreurs";
// import LivreursUpdate from "./pages/livreurs/livreursUpdate";
// import Roles from "./pages/roles/roles";
// import RolesCreate from "./pages/roles/rolesCreate";
// import RolesUpdate from "./pages/roles/rolesUpdate";
// import BlackList from "./pages/blacklist/blackList";
// import Payments from "./pages/payments/payments";
// import PaymentsSallers from "./pages/payments/sallers/page";
// import PaymentsConfirmators from "./pages/payments/confirmators/page";
// import PaymentsLivreurs from "./pages/payments/livreurs/page";
// import PaymentsSystem from "./pages/payments/system/page";
// import IntegrationDelivery from "./pages/integration/delivery/page";
// import IntegrationApps from "./pages/integration/apps/page";
// import Reclamations from "./pages/reclamations/page";
// import Annonces from "./pages/annonces/page";
// import AnnoncesCreate from "./pages/annonces/create/page";
// import AnnoncesUpdate from "./pages/annonces/update/page";
// import Cart from "./pages/cart/page";
// import Profile from "./pages/profile/page";
// import UserCreate from "./pages/users/create/page";

// const Home = lazy(() => import(`./websites/${appConfig.slug}/page`));
// const About = lazy(() => import(`./websites/${appConfig.slug}/about/page`));
// const Contact = lazy(() => import(`./websites/${appConfig.slug}/contact/page`));
// const Policy = lazy(() => import(`./websites/${appConfig.slug}/policy/page`));

// function Maintenance({ onUnlock }) {
//   const [password, setPassword] = useState("");

//   const handleSubmit = () => {
//     if (password === "456789") {
//       onUnlock();
//     } else {
//       alert("Wrong password");
//     }
//   };

//   return (
//     <div className="h-screen flex flex-col items-center justify-center bg-gray-100 px-4">
//       <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-md">
//         <div className="flex justify-center">
//           <div className="relative">
//               <div className="w-24 h-24 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>

//               <div className="absolute inset-0 flex items-center justify-center text-4xl">
//                 🔧
//               </div>
//             </div>
//           </div>

//           <h1 className="text-xl font-bold text-center text-gray-600 my-2">
//             Maintenance Mode
//           </h1>

//           <p className="text-center text-xs text-gray-300 mb-6">
//             We are improving the website.
//             <br />
//             Please enter the access password.
//           </p>

//         <input
//           type="password"
//           placeholder="Enter password"
//           className="w-full text-xs outline-0 border rounded-lg p-2.5 mb-4"
//           value={password}
//           onChange={(e) => setPassword(e.target.value)}
//           onKeyDown={(e) => {
//             if (e.key === "Enter") {
//               handleSubmit();
//             }
//           }}
//         />

//         <button
//           onClick={handleSubmit}
//           className="w-full bg-blue-600 text-white py-2.5 text-xs rounded-lg"
//         >
//           Access Website
//         </button>
//       </div>
//     </div>
//   );
// }

// const AppRoutes = () => {
//   const { token } = useAuth();

//   return (
//     <Suspense fallback={<div className="h-screen"></div>}>
//       <Routes>
//         <Route element={<WebLayout />}>
//           <Route path="/" element={<Home />} />
//           <Route path="about" element={<About />} />
//           <Route path="contact" element={<Contact />} />
//           <Route path="policy" element={<Policy />} />
//         </Route>

//         <Route element={<AuthLayout />}>
//           <Route path="login" element={<LoginPage />} />
//           <Route path="register" element={<RegisterPage />} />
//         </Route>

//         <Route
//           element={
//             token ? (
//               <DashboardLayout />
//             ) : (
//               <Navigate to="/login" replace />
//             )
//           }
//         >
//           <Route path="dashboard" element={<Dashboard />} />
//           <Route path="orders/:status" element={<Orders />} />
//           <Route path="orders/update/:id" element={<OrdersUpdate />} />
//           <Route path="products" element={<Products />} />
//           <Route path="products/:id" element={<ProductDetail />} />
//           <Route path="products/create" element={<ProductCreate />} />
//           <Route path="products/update/:id" element={<ProductUpdate />} />
//           <Route path="variants" element={<Varianrs />} />
//           <Route path="variants/create" element={<VarianrsCreate />} />
//           <Route path="variants/update/:id" element={<VarianrsUpdate />} />
//           <Route path="categories" element={<Categories />} />
//           <Route path="categories/create" element={<CategoriesCreate />} />
//           <Route path="categories/update/:id" element={<CategoriesUpdate />} />
//           <Route path="cities" element={<Cities />} />
//           <Route path="configs" element={<Configs />} />
//           <Route path="scan" element={<Scan />} />
//           <Route path="admins" element={<Admins />} />
//           <Route path="admins/:id" element={<AdminsUpdate />} />
//           <Route path="sallers" element={<Saller />} />
//           <Route path="sallers/:id" element={<SallerUpdate />} />
//           <Route path="confirmators" element={<Confirmators />} />
//           <Route path="confirmators/:id" element={<ConfirmatorsUpdate />} />
//           <Route path="fournisseurs" element={<Fournisseurs />} />
//           <Route path="fournisseurs/create" element={<FournisseursCreate />} />
//           <Route
//             path="fournisseurs/update/:id"
//             element={<FournisseursUpdate />}
//           />
//           <Route path="livreurs" element={<Livreurs />} />
//           <Route path="livreurs/update/:id" element={<LivreursUpdate />} />
//           <Route path="roles" element={<Roles />} />
//           <Route path="roles/create" element={<RolesCreate />} />
//           <Route path="roles/update/:id" element={<RolesUpdate />} />
//           <Route path="black-list" element={<BlackList />} />
//           <Route path="payments" element={<Payments />} />
//           <Route path="payments/sallers" element={<PaymentsSallers />} />
//           <Route
//             path="payments/confirmators"
//             element={<PaymentsConfirmators />}
//           />
//           <Route path="payments/livreurs" element={<PaymentsLivreurs />} />
//           <Route path="payments/system" element={<PaymentsSystem />} />
//           <Route
//             path="integration/delivery"
//             element={<IntegrationDelivery />}
//           />
//           <Route
//             path="integration/applications"
//             element={<IntegrationApps />}
//           />
//           <Route path="reclamations" element={<Reclamations />} />
//           <Route path="annonces" element={<Annonces />} />
//           <Route path="annonces/create" element={<AnnoncesCreate />} />
//           <Route path="annonces/update/:id" element={<AnnoncesUpdate />} />
//           <Route path="cart" element={<Cart />} />
//           <Route path="profile" element={<Profile />} />
//           <Route path="users/create" element={<UserCreate />} />
//         </Route>

//         <Route
//           path="*"
//           element={<Navigate to={token ? "/dashboard" : "/"} replace />}
//         />
//       </Routes>
//     </Suspense>
//   );
// };

// function App() {
//   const [display, setDisplay] = useState(false);

//   if (!display) {
//     return <Maintenance onUnlock={() => setDisplay(true)} />;
//   }

//   return (
//     <BrowserRouter>
//       <AuthProvider>
//         <LanguageProvider>
//           <ScrollToTop />
//           <AppRoutes />
//         </LanguageProvider>
//       </AuthProvider>
//     </BrowserRouter>
//   );
// }

// export default App;